import { createRef, PureComponent } from "react";

import { reactor, ReactorConfiguration } from "@app/arc";
import { AccuracyReactorConfiguration, AdequacyReactorConfiguration, AttestationReactorConfiguration } from "@app/arc/configuration";

interface ArcReactorProps {
  config: ReactorConfiguration;
}

export default class ArcReactor extends PureComponent<ArcReactorProps> {
  private svg = createRef<SVGSVGElement>();

  public componentDidMount() {
    reactor(this.svg.current, this.props.config);
  }

  public componentDidUpdate() {
    reactor(this.svg.current, this.props.config);
  }

  public render() {
    const title = "Arc Reactor";
    const description = getArcReactorDescription(this.props.config);
    return (
      <svg ref={this.svg} role="img" aria-label={`${description}`}>
        <title>{title}</title>
        <desc>{description}</desc>
      </svg>
    );
  }
}


export const getArcReactorDescription = (config: ReactorConfiguration): string => {
  if (!config) {
    return "";
  }

  let baseString = "";
  let type = "";
  let segmentCount = 0;
  const withAttestation = config.attestation ? " with Attestation" : "";

  if (config.adequacy && config.accuracy) {
    type = "Composite";
    segmentCount = 2;
  } else if (config.adequacy && !config.accuracy) {
    type = "Adequacy";
    segmentCount = 1;
  } else if (!config.adequacy && config.accuracy) {
    type = "Accuracy";
    segmentCount = 1;
  }

  if (withAttestation) {
    segmentCount += 1;
  }

  baseString += `${type} Arc Reactor. A radial bar chart with ${segmentCount} section(s).`;
  baseString += getAccuracySegment(config.accuracy);
  baseString += getAdequacySegment(config.adequacy);
  baseString += getAttestationSegment(config.attestation);

  return baseString;
}

const getAccuracySegment = (accuracy: AccuracyReactorConfiguration) => {
  if (!accuracy) {
    return "";
  }

  let accuracyString = ` The accuracy section shows an accuracy index of ${accuracy.index}. This project's data was`;
  const bandsTotal = accuracy.bands.reduce((total, current) => {
    total += current.value;
    return total;
  }, 0);

  // we don't need to add commas because .map returns an array, and that's
  // stringified with commas between each item 
  const bandsText = accuracy.bands.map((band, index, arr) => {
    let bandString = "";
    if (index === arr.length - 1) {
      bandString += " and";
    }

    bandString += ` ${getAccuracyPercentageString(bandsTotal, band.value)} ${band.name}`
    return bandString;
  })

  accuracyString += bandsText + ".";

  return accuracyString;
}

const getAdequacySegment = (adequacy: AdequacyReactorConfiguration) => {
  if (!adequacy) {
    return "";
  }


  let adequacyString = ` The adequacy section shows an adequacy index of ${adequacy.index}.`;
  adequacyString += ` ${adequacy.green.toFixed(2)}% of county/specialty groups meet access and servicing criteria, ${adequacy.yellow.toFixed(2)}% only meet one, and ${adequacy.red.toFixed(2)}% do not meet any criteria.`;
  if (adequacy.blue > 0) {
    adequacyString += ` ${adequacy.blue.toFixed(2)}% of county/specialty groups pass by approved exceptions.`
  }
  if (adequacy.projected > 0) {
    adequacyString += ` ${adequacy.projected.toFixed(2)}% of county/specialty groups are projeced to pass with provider changes, which would bring the adequacy index to ${adequacy.projectedIndex}.`
  }

  return adequacyString;
}

const getAttestationSegment = (attestation: AttestationReactorConfiguration) => {
  if (!attestation) {
    return "";
  }

  let attestationString = ` The attestation section shows an attestation rate of ${attestation.green}%`;

  attestationString += `, based on a ${attestation.attestationRateDays || 365}-day attestation rate`

  attestationString += ".";

  return attestationString;
}

const getAccuracyPercentageString = (totalCount: number, currentCount: number) => {
  const value = currentCount / totalCount;
  let valueString: string;
  if (value > 0 && value < 0.01) {
    valueString = "less than 0.1%";
  } else {
    valueString = `${(Math.round(value * 100))}%`;
  }

  return valueString;
}